import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

import { cn } from "@/lib/utils"

/* V_DESIGN_OVERHAUL_V3 — solid blue primary, soft accent secondary, ghost,
 * destructive in soft red. Bold weight, soft rounding, focus ring uses
 * accent. Active state subtly compresses (active:scale-[0.98]). */
const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center gap-2 rounded-md border border-transparent bg-clip-padding text-sm font-semibold whitespace-nowrap transition-all duration-fast outline-none select-none focus-visible:ring-4 focus-visible:ring-accent/20 focus-visible:border-accent/60 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-error aria-invalid:ring-4 aria-invalid:ring-error/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "bg-accent text-accent-foreground shadow-sm hover:bg-accent-hover hover:shadow-md",
        outline:
          "border-border-default bg-bg-card text-text-primary hover:bg-bg-subtle hover:border-text-tertiary",
        secondary:
          "bg-accent-light text-accent border border-accent/20 hover:bg-accent-light hover:border-accent/40",
        ghost:
          "text-text-secondary hover:bg-bg-subtle hover:text-text-primary aria-expanded:bg-bg-subtle aria-expanded:text-text-primary",
        destructive:
          "bg-error-bg text-error-text border border-error/20 hover:bg-error/10 hover:border-error/40",
        link: "text-accent underline-offset-4 hover:underline",
      },
      size: {
        default:
          "h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        xs: "h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        icon: "size-8",
        "icon-xs":
          "size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        "icon-sm":
          "size-7 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg",
        "icon-lg": "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

type AnchorButtonProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  asChild: true
  variant?: VariantProps<typeof buttonVariants>["variant"]
  size?: VariantProps<typeof buttonVariants>["size"]
  className?: string
  children?: React.ReactNode
  disabled?: boolean
}

type NativeButtonProps = ButtonPrimitive.Props & VariantProps<typeof buttonVariants> & {
  asChild?: false
}

type ButtonProps = NativeButtonProps | AnchorButtonProps

function Button({ className, variant = "default", size = "default", ...props }: ButtonProps) {
  if ("asChild" in props && props.asChild) {
    const { asChild, children, ...rest } = props
    void asChild
    return (
      <Slot
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className }))}
        {...(rest as Omit<AnchorButtonProps, "asChild" | "children" | "variant" | "size" | "className">)}
      >
        {children}
      </Slot>
    )
  }
  const { children, ...rest } = props as NativeButtonProps
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...rest}
    >
      {children}
    </ButtonPrimitive>
  )
}

export { Button, buttonVariants }
